import { api, apiClientV2, unwrapV2Response } from './index.js';

// The client writes every key it is given, so an absent one must be left out
// rather than sent as "undefined".
const definedQuery = query =>
	Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined));

/**
 * Fetches the tile grid of a project from the v2 grids endpoint.
 *
 * For an orthophoto project the server sizes the tiles from the taxonomy node
 * (`node_uuid`); a single-image project returns one tile per image and reads no
 * node. `model_uuid` is the legacy Model view key.
 *
 * @async
 * @param {Object} params
 * @param {string} params.project_uuid - The UUID of the project.
 * @param {string} [params.model_uuid] - The UUID of the model (Model view).
 * @param {string} [params.node_uuid] - The UUID of the taxonomy node (Collection view).
 * @returns {Promise<{grid_geojson: Object, grid_size: number}>} The grid data.
 */
export const getGrid = async ({ project_uuid, model_uuid, node_uuid }) => {
	const { data, error } = await apiClientV2.get(
		'/grids',
		definedQuery({ project_uuid, model_uuid, node_uuid })
	);

	if (error) {
		throw new Error(error.message || 'Failed to fetch grid');
	}

	return unwrapV2Response(data);
};

/**
 * Returns the model grid tiles as GeoJSON features, or an empty list when the grid cannot be loaded.
 */
export const getGridFeatures = async params => {
	try {
		const { grid_geojson } = await getGrid(params);
		return grid_geojson?.features ?? [];
	} catch (error) {
		return [];
	}
};

export const getUnrotatedGrid = async projectId => {
	const { data, error } = await apiClientV2.get(`/grids/unrotated?project_uuid=${projectId}`);
	if (error) throw error;
	return unwrapV2Response(data);
};

export const getRotatedGrid = async projectId => {
	const { data, error } = await apiClientV2.get(`/grids/rotated?project_uuid=${projectId}`);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Adds an annotation to the grid for a specific project, model and file
 *
 * @async
 * @param {Object} params - The parameters for adding an annotation.
 * @param {string} params.projectUuid - The UUID of the project.
 * @param {string} params.filename - The name of the file to add the annotation.
 * @param {string} params.classname - The class name for the annotation.
 * @param {string} params.classid - The class ID for the annotation.
 * @param {string} params.modelUuid - The UUID of the model.
 * @param {Array|Object} params.polygon - The normalized polygon data for the annotation.
 * @returns {Promise<Object>} The data returned from the API.
 */
export const addAnnotationToGrid = async ({
	projectUuid,
	filename,
	classname,
	classid,
	modelUuid,
	polygon,
}) => {
	const { data } = await api.post(`/grid/${projectUuid}/point_px`, {
		filename,
		classname,
		classid,
		model_uuid: modelUuid,
		polygon,
	});

	return data;
};

/**
 * Deletes an annotation from the grid for a specific project and model
 *
 * @async
 * @param {Object} params - The parameters for deleting an annotation
 * @param {string} params.projectUuid - UUID of the project
 * @param {string} params.modelUuid - UUID of the model
 * @param {string} params.annotationUuid - UUID of the annotation to be deleted
 * @returns {Promise<Object>} The data returned from the API.
 */
export const deleteAnnotationFromGrid = async ({ projectUuid, modelUuid, annotationUuid }) => {
	const { data } = await api.delete(`/grid/${projectUuid}/annotation`, {
		data: {
			model_uuid: modelUuid,
			annotation_uuid: annotationUuid,
		},
	});
	return data;
};
